import { LanguageTemplate } from "../template";
import { ActionsHandler, GetActionBuilder } from "../types";

const action_builder: ActionsHandler = {
  create: {
    title: (type: string) => `Create ${type.toLowerCase()}`,
    success: (type: string) => `${type} created`,
  },
  update: {
    title: (type: string) => `Edit ${type.toLowerCase()}`,
    success: (type: string) => `${type} edited`,
  },
  delete: {
    title: (type: string) => `Delete ${type.toLowerCase()}`,
    success: (type: string) => `${type} deleted`,
  },
};

export const en_us: LanguageTemplate = {
  moment_lang_key: "en",
  langs: {
    de_de: "German",
    en_us: "English US",
  },
  actions: {
    logout: "Logout",
    save: "Save",
    show: "Show",
    edit: "Edit",
    create: "Create",
    delete: "Delete",
    add: "Add",
    label: "Actions",
    open: "Open",
    close: "Close",
    cancel: "Cancel",
    copy: "Copied",
    toCopy: "Copy",
    markProcessed: "Mark processed",
    markPickedup: "Pickedup",
    printLabel: "Print label",
    print: "Print",
    showArticles: "Show articles",
    select: (t) => (t ? `Select ${t}` : "Select"),
    days: (d) => `${d} days`,
    followLink: "Follow link",
    builder: action_builder,
  },
  menu: {
    supply: {
      article_sources: "Article Sources",
      distributors: "Distributors",
      root_label: "Purchasing",
    },
  },
  results: {
    saved: "Saved",
    deleted: "Deleted",
  },
  errors: {
    generic: "An unkown error occurred",
    schema: {
      invalid: "Invalid information",
      not_found: {
        data: "Dataset not found",
        param: "Parameter missing",
      },
      unique: (keys, model) =>
        `A ${model} with the combination of '${keys.join(
          ", "
        )}' already exists`,
    },
    loginError: "An error occoured during login",
    login: { invalid: "Invalid login information" },
    no_items: "No items",
    "auth.invalid.information": "Invalid login information",
  },
  pages: {
    profile: {
      title: "Profile",
      update_success: "Profile updated",
    },
    login: {
      password: "Password",
      userId: "User ID",
      title: "Login",
      success: "Login success!",
    },
    dashboard: {
      all_articles: "All articles",
      article_view: "Article List",
    },
    footprint: {
      list: "Footprints",
      actions: GetActionBuilder("Footprint", action_builder),
    },
    location: {
      list: "Locations",
      actions: GetActionBuilder("Location", action_builder),
    },
    productGroup: {
      list: "Product group",
      actions: GetActionBuilder("Product group", action_builder),
    },
    articleCategory: {
      label: "Article category",
      list: "Article categories",
      actions: GetActionBuilder("Article category", action_builder),
    },
    keyField: {
      list: "Key fields",
      label: "Key field",
      actions: GetActionBuilder("Key field", action_builder),
    },
    article: {
      list: "Articles",
      view: "Article properties",
      actions: GetActionBuilder("Article", action_builder),
      parameter: {
        fix_required: "Fix",
        fix_required_tooltip: "Add missing required Parameter",
      },
    },
    distributor: {
      list: "Distributors",
      actions: GetActionBuilder("Distributor", action_builder),
    },
    articleSource: {
      list: "Part sources",
      view: "Part source",
      actions: GetActionBuilder("Part source", action_builder),
    },
    articleListView: {
      label: "Article view",
      select_category: "Please select article group",
      no_items_in_category: "This article group is empty",
    },
    customerOrder: {
      actions: GetActionBuilder("Customer order", action_builder),
      list: "Customer order list",
      view: "Customer order",
      orderPositions: "Positions",
    },
    customer: {
      menu_label: "Customer",
      list: {
        label: "Customer list",
      },
      actions: GetActionBuilder("Customer", action_builder),
    },
    userlist: {
      label: "User management",
      actions: GetActionBuilder("User", action_builder),
    },
  },
  global: {
    langSelector: {
      language: "Language",
    },
    deleteModal: {
      warning: (id: number, type: string, display?: string) => (
        <>
          <strong>Attention! this action can't be undone!</strong>
          <p>Are you sure that you want to delete the following item:</p>
          {display ? <p>{display}</p> : <></>}
          <p>
            Typ: {type} / ID: {id}
          </p>
        </>
      ),
    },
  },
  data: {
    none: "None",
    not_set: "Not set",
    footprint: {
      label: "Footprint",
      type: "Type",
    },
    location: {
      label: "Location",
      parent: "Parent",
    },
    product_group: {
      label: "Product group",
      parent: "Parent group",
    },
    articleCategory: {
      label: "Part category",
      meta: "Meta fields",
    },
    articleSource: {
      label: "Part source",
      label_plural: "Part sources",
      order_number: "Order number",
      url: "Web URL",
      moq: "Minimum order quantity",
    },
    customer: {
      company: "Company",
      label: "Customer",
      name: "Name",
      address: "Address",
    },
    distributor: {
      label: "Distributor",
      search_url: "Search Url",
      short_name: "Shortname",
    },
    inventory_type: {
      label: "Inventory type",
      types: {
        journal: "Journal",
        static: "Static",
      },
    },
    article: {
      label: "Part",
      datasheet_url: "Datasheet URL",
      fields: "Parameter",
      picture: "Picture",
      matchcode: "Matchcode",
      datasheet: "Datasheet",
      price_information: {
        label: "Price information",
        price: {
          label: "Price",
          quantity: "Quantity",
          create_label: "Add price",
        },
        price_tier: {
          label: "Price tier",
          delete_label: (tier) => `Delete price tier ${tier}`,
        },
      },
    },
    keyfield: {
      label: "Key field",
      si: "SI",
      values: "Values",
      type: "Type",
      unit: "Unit",
      required: "Required",
      types: {
        float: "Number",
        select: "Select",
        text: "Text",
      },
    },
    id: "ID",
    display_name: "Display name",
    description: "Description",
    moq: "Minimum order quantity",
    quantity_scale: {
      label: "Quantity scale",
      list: "Quantity scale",
      price: "Price",
      quantity: "Quantity",
    },
    strings_select_empty: "Type to add elements",
    meta: "Meta",
    name: "Name",
    note: "Note",
    bool: {
      true: "Yes",
      false: "No",
    },
    required: "Required",
    timestamp: "Timestamp",
    history: {
      label: "History",
      types: {
        create: "Created",
        update: "Updated",
        delete: "Deleted",
        add: "Added",
        remove: "Removed",
      },
    },
    fields: {
      current: "Current",
      label: "Fields",
    },
    article_categories: {
      capacitor: "Capacitor",
      resistor: "Resistor",
    },
    units: {
      volt: "Volt",
      ohm: "Ohm",
      farad: "Farad",
      henry: "Henry",
      meter: "Meter",
    },
    customerOrder: {
      created_at: "Created at",
      customer: "Customer",
      customerContact: "Customer contact",
      customerName: "Customer name",
      customerReference: "Customer reference",
      express: "Express",
      delivery_date: "Planed delivery date",
      notes: "Notes",
      positions: "Positions",
      state: "State",
      type: "Type",
      clerk: "Clerk",
      updated_at: "Updated at",
      states: {
        accepted: "Accepted",
        pickedup: "Pickedup",
        processing: "Processing",
        ready_for_pickup: "Ready for pickup",
        wait_for_parts: "Wait for parts",
        wait_for_reply: "Wait for reply",
        canceled: "Canceled",
      },
      types: {
        inquiry: "Inquiry",
        part_order: "Part Order",
        repair: "Repair",
      },
      order_number: "Order number",
      text: "Order position - text",
    },
    user: {
      label: "User",
      displayname: "Displayname",
      email: "Email",
      id: "ID",
      username: "Username",
      password: "Password",
    },
    order_positions: {
      sum: "Sum",
    },
    permissions: {
      label: "Permissions",
      "*.*": "root",
    },
  },
};
